/**
 * useJoongangSchedule — 중앙(레미콘) 생산 예정 요약: 오늘 + 다음 영업일들 (내 업무 홈, B′ 미리보기).
 *
 * 한 번 읽기(폴링·구독 없음). CF 호출 없음 — Firestore 에 이미 있는 것만:
 *   - daily_shipment(단수)  rqcRepo.readBetween      → 현장 이름·현장 수 (qty = 예정)
 *   - daily_shipments(복수) dailyShipmentsRepo.listBetween → 날짜별 예정 합(gov/cust_planned_m3)
 * 합치는 규칙은 domain/mySchedule.ts buildJoongangDays.
 *
 * 범위: cron(rqcScheduledSync)이 오늘 −2 ~ +7 영업일을 미리 적재한다(2026-10-19 백엔드 변경, 운영 반영 전).
 *    자료가 없는 날은 결과 맵에 없고, 화면은 "ERP 예정 없음"으로 표시한다(0 이 아니다).
 * enabled=false(권한 없음·소속 아님)면 아무것도 읽지 않는다.
 */
import { useEffect, useMemo, useState } from "react";
import { rqcRepo } from "@/services/rqc.repository";
import { dailyShipmentsRepo } from "@/services/daily_shipments.repository";
import { buildJoongangDays, type ProdDay } from "@/domain/mySchedule";

interface Result {
  key: string;
  days: Map<string, ProdDay>;
  error: string | null;
}

export function useJoongangSchedule(enabled: boolean, dates: string[]) {
  const key = enabled && dates.length ? dates.join(",") : "";
  const [res, setRes] = useState<Result | null>(null);

  useEffect(() => {
    if (!key) return;
    const list = key.split(",");
    const from = list[0];
    const to = list[list.length - 1];
    let alive = true;
    void (async () => {
      const [rows, totals] = await Promise.allSettled([
        rqcRepo.readBetween(from, to),
        dailyShipmentsRepo.listBetween(from, to),
      ]);
      if (!alive) return;
      const planned = new Map<string, number>();
      if (totals.status === "fulfilled") {
        for (const d of totals.value) {
          if (d.total_planned_m3 != null) planned.set(d.id, d.total_planned_m3);
        }
      }
      const siteRows = rows.status === "fulfilled" ? rows.value : [];
      const failed = rows.status === "rejected" && totals.status === "rejected";
      setRes({
        key,
        days: buildJoongangDays(list, siteRows, planned),
        error: failed ? "생산 예정을 불러오지 못했습니다." : null,
      });
    })();
    return () => { alive = false; };
  }, [key]);

  const cur = res && res.key === key ? res : null;
  const empty = useMemo(() => new Map<string, ProdDay>(), []);
  return {
    days: cur?.days ?? empty,
    loading: !!key && !cur,
    error: cur?.error ?? null,
  };
}
